import { AppRoutes } from 'src/lib/routes';
import { NextSeo } from 'next-seo';
import WithDefaultJsonLd from 'src/components/WithDefaultJsonLd';
import ImageWithFallback from 'src/components/ImageWithFallback';
import styles from 'src/styles/modules/pages/about.module.scss';
import useBreakpoints from 'src/hooks/useBreakpoints';
import Link from 'next/link';

import Illu1Image from 'public/images/illu/illu_1.png';
import Illu2Image from 'public/images/illu/illu_2.png';
import Illu3Image from 'public/images/illu/illu_3.png';
import Illu4Image from 'public/images/illu/illu_4.png';
import Homework1Image from 'public/images/home/homework_1.png';
import Homework3Image from 'public/images/home/homework_3.png';
import Marquee from 'react-fast-marquee';
import ChainImage from 'public/images/about/chain.png';
import { useTranslations } from 'next-intl';
import { GetStaticProps } from 'next';
import { prepareTranslations } from 'src/lib/prepare_translations';
import useAddLocaleToUrl from 'src/hooks/useAddLocaleToUrl';

export const getStaticProps: GetStaticProps = async (context) => {
  const translations = await prepareTranslations(context, ['pages/about']);

  return { props: translations }
};

const About = () => {
  const addLocaleToUrl = useAddLocaleToUrl()
  const translation = useTranslations('pages/about')
  const { isScreenMd } = useBreakpoints();
  return (
    <>
      <NextSeo
        title={translation('metaTitle')}
        description={translation('metaDescription')}
        canonical={addLocaleToUrl(AppRoutes.aboutRoute().toUrl())}
        openGraph={{
          url: addLocaleToUrl(AppRoutes.aboutRoute().toUrl()),
          title: translation('metaTitle'),
          description: translation('metaDescription'),
        }}
      />
      <WithDefaultJsonLd />

      <div className="tw-bg-brand-yellow">
        <div className="container tw-max-w-3xl tw-text-center tw-py-16 md:tw-py-24">
          <h1 className="tw-font-styleSans tw-font-semibold tw-text-3xl md:tw-text-4xl tw-mb-8 md:tw-mb-16 tw-leading-none md:tw-leading-none tw-max-w-xs md:tw-max-w-lg tw-mx-auto">
            {translation('started')}
          </h1>

          <ImageWithFallback
            src={Illu1Image}
            alt=""
            className="tw-static tw-inline-block tw-w-auto tw-h-36 md:tw-h-80 tw-mb-8 md:tw-mb-16"
          />

          <div className="tw-font-styleSans tw-font-semibold tw-text-3xl md:tw-text-4xl tw-mb-8 md:tw-mb-16 tw-leading-none md:tw-leading-none tw-max-w-xs md:tw-max-w-lg tw-mx-auto">
            {translation('whenWeWerePlanning')}
          </div>

          <ImageWithFallback
            src={Illu2Image}
            alt=""
            className="tw-static tw-inline-block tw-w-auto tw-h-40 md:tw-h-80 tw-mb-8 md:tw-mb-16"
          />

          <div className="tw-font-styleSans tw-font-semibold tw-text-3xl md:tw-text-4xl tw-mb-8 md:tw-mb-16 tw-leading-none md:tw-leading-none tw-max-w-xs md:tw-max-w-lg tw-mx-auto">
            {translation('butHeTookUs')}
          </div>

          <ImageWithFallback
            src={Illu3Image}
            alt=""
            className="tw-static tw-inline-block tw-w-auto tw-h-32 md:tw-h-80 tw-mb-8 md:tw-mb-16"
          />

          <div className="tw-font-styleSans tw-font-semibold tw-text-3xl md:tw-text-4xl tw-mb-8 md:tw-mb-16 tw-leading-none md:tw-leading-none tw-max-w-xs md:tw-max-w-lg tw-mx-auto">
            {translation('thatTripLeft')}
          </div>

          <ImageWithFallback
            src={Illu4Image}
            alt=""
            className="tw-static tw-inline-block tw-w-auto tw-h-36 md:tw-h-80"
          />
        </div>
      </div>

      <div className="container tw-max-w-3xl tw-py-16 md:tw-py-24">
        <div className="row">
          <div className="col-md-5 tw-text-center">
            <ImageWithFallback
              src={Homework1Image}
              alt=""
              className="tw-static tw-inline-block tw-h-48 tw-w-auto tw-mb-8"
            />

            <h2 className="tw-font-styleSans tw-font-semibold tw-text-2xl tw-mb-2 tw-leading-none">
              {translation('theSeekSophieDifference')}
            </h2>

            <div>
              {translation('weAreAMaketplace')}
            </div>

            <div className="tw-text-center tw-mt-8">
              <Link legacyBehavior prefetch={false} href={AppRoutes.ourCurationRoute().toPath()}>
                <a className="btn btn-primary tw-rounded-4xl tw-py-3 tw-px-8">{translation('seeOurCurationProcess')}</a>
              </Link>
            </div>
          </div>
          <div className="offset-md-2 col-md-5 tw-text-center tw-pt-16 md:tw-pt-36">
            <ImageWithFallback
              src={Homework3Image}
              alt=""
              className="tw-static tw-inline-block tw-h-48 tw-w-auto tw-mb-8"
            />

            <h2 className="tw-font-styleSans tw-font-semibold tw-text-2xl tw-mb-2 tw-leading-none">
              {translation('travelAsAForce')}
            </h2>

            <div>
              {translation('tooMuchOfTravel')}
            </div>

            <div className="tw-text-center tw-mt-8">
              <Link legacyBehavior prefetch={false} href={AppRoutes.ourMissionRoute().toPath()}>
                <a className="btn btn-primary tw-rounded-4xl tw-py-3 tw-px-8">{translation('seeOurMission')}</a>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="tw-bg-brand-navy tw-py-16 md:tw-py-24">
        <h2 className="tw-text-brand-green tw-text-5xl md:tw-text-6xl tw-font-styleSans tw-font-semibold tw-mb-8 tw-leading-none tw-text-center">{translation('whoIsSophie')}</h2>
        <div className="tw-flex tw-flex-col">
          <div className="container tw-max-w-xl tw-text-center tw-text-brand-green tw-order-2 md:tw-order-1 tw-mt-8 md:tw-mt-0 tw-mb-0 md:tw-mb-16">
            <div className="tw-mb-6">
              {translation('weGetAsked')}
            </div>
            <div className="tw-mb-6">
              {translation('meansWisdom')}
            </div>
            <div className="">
              {translation('weAreSmallTeam')}
            </div>
          </div>
          <div className="tw-bg-repeat-x tw-bg-cover tw-bg-center xl:tw-bg-contain tw-h-48 md:tw-h-80 tw-w-full tw-order-1 md:tw-order-2">
            <Marquee>
              <ImageWithFallback className="tw-object-cover tw-static tw-h-48 md:tw-h-80 tw-w-auto" src={ChainImage} alt="Seek Sophie About Image"/>
              <ImageWithFallback className="tw-object-cover tw-static tw-h-48 md:tw-h-80 tw-w-auto" src={ChainImage} alt="Seek Sophie About Image"/>
              <ImageWithFallback className="tw-object-cover tw-static tw-h-48 md:tw-h-80 tw-w-auto" src={ChainImage} alt="Seek Sophie About Image"/>
            </Marquee>
          </div>
        </div>
      </div>
    </>
  );
};

export default About;
